"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheStorySoFar() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const thumbnails = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/maternity_shoot_garden.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/maternity_shoot_garden.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/maternity_shoot_garden.png-wvc-sizes",
    alt: "Expectant parents in a sunlit garden",
    occasion: "Golden Hour Glow"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/baby_nursery_details.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/baby_nursery_details.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/baby_nursery_details.png-wvc-sizes",
    alt: "Soft sage nursery details",
    occasion: "Preparing the Nest"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/maternity_portrait_editorial.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/maternity_portrait_editorial.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/maternity_portrait_editorial.png-wvc-sizes",
    alt: "Editorial maternity portrait",
    occasion: "The Wait Begins"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/parents_to_be_candid.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/parents_to_be_candid.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/parents_to_be_candid.png-wvc-sizes",
    alt: "Candid moment of the parents-to-be",
    occasion: "Pure Anticipation"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/baby_shower_invitation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/baby_shower_invitation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/baby_shower_invitation.png-wvc-sizes",
    alt: "Minimalist baby shower invitation",
    occasion: "Save the Date"
  }];
  return <section data-section-id="172" id="the-story-so-far" className="relative overflow-hidden bg-background text-foreground py-24 md:py-32 lg:py-[140px]">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center select-none">
        <span className="font-serif italic text-[20vw] leading-none tracking-tight rotate-[-12deg] whitespace-nowrap" style={{
        color: "hsl(145 30% 25% / 0.05)"
      }}>
          ohlson
        </span>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={leftRef} className={`lg:col-span-7 lg:-ml-6 xl:-ml-12 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative">
              <div className="relative rotate-[-2deg] hover:rotate-0 transition-transform duration-700 ease-out" style={{
              filter: "drop-shadow(0 30px 40px hsl(145 30% 25% / 0.1))"
            }}>
                <div className="overflow-hidden rounded-md bg-card border border-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/maternity_hero_portrait.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/maternity_hero_portrait.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/maternity_hero_portrait.png-wvc-sizes" alt="Expectant mother in a soft sage dress" loading="lazy" decoding="async" className="w-full h-auto aspect-[3/4] object-cover" />
                </div>
                {}
                <div aria-hidden="true" className="absolute -top-3 left-1/2 -translate-x-1/2 w-24 h-6 rotate-[3deg] opacity-60" style={{
                backgroundColor: "hsl(135 20% 85% / 0.5)"
              }} />
              </div>
              <p className="mt-6 ml-4 font-serif italic text-lg md:text-xl text-muted-foreground" style={{
              transform: "rotate(-1.5deg)"
            }}>The Beginning — Autumn 2026</p>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 lg:pt-8 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-8">
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-primary">
                A New Chapter Begins
              </p>

              <h2 className="font-default text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
                For the love
                <br />
                <span className="italic font-light text-primary">
                  already growing.
                </span>
              </h2>

              <div className="space-y-5 text-base md:text-lg leading-relaxed text-muted-foreground font-default max-w-lg">
                <p>
                  Our journey to parenthood has been a quiet collection of small wonders. From the first
                  flutter to the final weeks of waiting, we are documenting every heartbeat and hope
                  as we prepare to welcome our little one into the Ohlson family.
                </p>
                <p>
                  We invite you to join us for a morning of brunch and celebration. This gathering is
                  more than a shower; it is the first of many memories we hope to build with the
                  community that will surround Baby Ohlson with love.
                </p>
              </div>

              {}
              <div className="pt-2">
                
              </div>

              {}
              
            </div>
          </div>
        </div>

        {}
        <div aria-hidden="true" className="hidden md:block absolute bottom-0 right-6 lg:right-10">
          <div className="relative w-32 h-32 lg:w-36 lg:h-36 rounded-lg flex items-center justify-center" style={{
          animation: "spin 30s linear infinite"
        }}>
            <svg viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <path id="circle-path" d="M 100, 100 m -75, 0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0" fill="none" />
              </defs>
              <text style={{
              fontFamily: "var(--font-mono)",
              fontSize: "13px",
              letterSpacing: "0.35em",
              textTransform: "uppercase",
              fill: "hsl(145 30% 25%)"
            }}>
                <textPath href="#circle-path" startOffset="0">
                  Welcome &middot; Baby &middot; Ohlson &middot; 2024 &nbsp;
                </textPath>
              </text>
            </svg>
            <div className="absolute inset-0 m-auto w-3 h-3 rounded-lg" style={{
            backgroundColor: "hsl(145 30% 25%)"
          }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </section>;
}